import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TerminalAppearanceAPI } from '../../../constants/API';
import { TerminalTabloElementSettings } from '../../../types/Admin/TerminalAppearanceTypes';
import styles from './BeltaRssWidget.module.scss';

const DEFAULT_NEWS_COUNT = 5;
const DEFAULT_SWITCH_SEC = 10;
const DEFAULT_REFRESH_MIN = 10;
const MIN_NEWS_COUNT = 1;
const MAX_NEWS_COUNT = 20;
const MIN_SWITCH_SEC = 1;
const MAX_SWITCH_SEC = 120;
const MIN_REFRESH_MIN = 1;
const MAX_REFRESH_MIN = 120;

/** Скорость автопрокрутки длинного текста (px/сек). */
const SCROLL_PX_PER_SEC = 28;
/** Пауза вверху и внизу перед сменой направления. */
const SCROLL_EDGE_PAUSE_MS = 1400;
/** Задержка старта прокрутки после смены новости (после fade-in). */
const SCROLL_START_DELAY_MS = 400;

type BeltaRssItem = {
  title: string;
  description?: string;
  link?: string;
  category?: string;
  pubDate?: string;
};

type BeltaRssResponse = {
  items: BeltaRssItem[];
};

type BeltaRssWidgetProps = {
  element: TerminalTabloElementSettings;
};

const clamp = (value: number, min: number, max: number, fallback: number) => {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
};

export const BeltaRssWidget = ({ element }: BeltaRssWidgetProps) => {
  const newsCount = clamp(
    Number(element.rssNewsCount),
    MIN_NEWS_COUNT,
    MAX_NEWS_COUNT,
    DEFAULT_NEWS_COUNT
  );
  const switchIntervalMs =
    clamp(
      Number(element.rssSwitchIntervalSeconds),
      MIN_SWITCH_SEC,
      MAX_SWITCH_SEC,
      DEFAULT_SWITCH_SEC
    ) * 1000;
  const refreshIntervalMs =
    clamp(
      Number(element.rssRefreshPeriodMinutes),
      MIN_REFRESH_MIN,
      MAX_REFRESH_MIN,
      DEFAULT_REFRESH_MIN
    ) *
    60 *
    1000;

  const [items, setItems] = useState<BeltaRssItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fadeIn, setFadeIn] = useState(true);
  const unmountedRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const fetchNews = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const res = await fetch(TerminalAppearanceAPI.getBeltaRss(newsCount), {
        signal: controller.signal
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data: BeltaRssResponse = await res.json();
      if (unmountedRef.current) return;
      const nextItems = Array.isArray(data?.items) ? data.items.filter((i) => i?.title) : [];
      setItems(nextItems);
      setCurrentIndex(0);
      setError(nextItems.length === 0 ? 'Нет новостей' : null);
    } catch (e) {
      if (unmountedRef.current) return;
      if (e instanceof Error && e.name === 'AbortError') return;
      setError('Ошибка загрузки');
    } finally {
      if (!unmountedRef.current) {
        setLoading(false);
      }
    }
  }, [newsCount]);

  useEffect(() => {
    unmountedRef.current = false;
    setLoading(true);
    fetchNews();
    const id = setInterval(fetchNews, refreshIntervalMs);
    return () => {
      unmountedRef.current = true;
      clearInterval(id);
      abortRef.current?.abort();
    };
  }, [fetchNews, refreshIntervalMs]);

  useEffect(() => {
    if (items.length <= 1) return;
    const id = setInterval(() => {
      setFadeIn(false);
      window.setTimeout(() => {
        if (unmountedRef.current) return;
        setCurrentIndex((prev) => (prev + 1) % items.length);
        setFadeIn(true);
      }, 250);
    }, switchIntervalMs);
    return () => clearInterval(id);
  }, [items.length, switchIntervalMs]);

  const current = items[currentIndex];
  const titleSizePx = Math.min(48, Math.max(10, element.fontSize ?? 16));

  /** Автопрокрутка, если текст не помещается в контейнер. */
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || !current?.title || !fadeIn) return;

    viewport.scrollTop = 0;
    let cancelled = false;
    let rafId = 0;
    let startTimeout = 0;
    let resizeObserver: ResizeObserver | null = null;

    type Phase = 'pauseTop' | 'down' | 'pauseBottom' | 'up';
    let phase: Phase = 'pauseTop';
    let phaseStartedAt = 0;
    let lastTs = 0;
    let scrollPos = 0;

    const getOverflow = () => {
      const content = contentRef.current;
      if (!content) return 0;
      return Math.max(0, content.scrollHeight - viewport.clientHeight);
    };

    const tick = (now: number) => {
      if (cancelled) return;
      if (!lastTs) lastTs = now;
      const dt = Math.min(64, now - lastTs);
      lastTs = now;

      const overflow = getOverflow();
      if (overflow <= 1) {
        viewport.scrollTop = 0;
        scrollPos = 0;
        rafId = requestAnimationFrame(tick);
        return;
      }

      if (!phaseStartedAt) phaseStartedAt = now;

      if (phase === 'pauseTop') {
        scrollPos = 0;
        viewport.scrollTop = 0;
        if (now - phaseStartedAt >= SCROLL_EDGE_PAUSE_MS) {
          phase = 'down';
          phaseStartedAt = now;
        }
      } else if (phase === 'down') {
        scrollPos = Math.min(overflow, scrollPos + (SCROLL_PX_PER_SEC * dt) / 1000);
        viewport.scrollTop = scrollPos;
        if (scrollPos >= overflow - 0.5) {
          scrollPos = overflow;
          viewport.scrollTop = overflow;
          phase = 'pauseBottom';
          phaseStartedAt = now;
        }
      } else if (phase === 'pauseBottom') {
        if (now - phaseStartedAt >= SCROLL_EDGE_PAUSE_MS) {
          phase = 'up';
          phaseStartedAt = now;
        }
      } else if (phase === 'up') {
        scrollPos = Math.max(0, scrollPos - (SCROLL_PX_PER_SEC * dt) / 1000);
        viewport.scrollTop = scrollPos;
        if (scrollPos <= 0.5) {
          scrollPos = 0;
          viewport.scrollTop = 0;
          phase = 'pauseTop';
          phaseStartedAt = now;
        }
      }

      rafId = requestAnimationFrame(tick);
    };

    const start = () => {
      if (cancelled) return;
      scrollPos = 0;
      viewport.scrollTop = 0;
      phase = 'pauseTop';
      phaseStartedAt = 0;
      lastTs = 0;
      rafId = requestAnimationFrame(tick);
    };

    startTimeout = window.setTimeout(start, SCROLL_START_DELAY_MS);

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        const overflow = getOverflow();
        if (overflow <= 1) {
          scrollPos = 0;
          viewport.scrollTop = 0;
        } else if (scrollPos > overflow) {
          scrollPos = overflow;
          viewport.scrollTop = overflow;
        }
      });
      resizeObserver.observe(viewport);
      if (contentRef.current) resizeObserver.observe(contentRef.current);
    }

    return () => {
      cancelled = true;
      window.clearTimeout(startTimeout);
      cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
      viewport.scrollTop = 0;
    };
  }, [current?.title, currentIndex, fadeIn, titleSizePx]);

  const textStyle: React.CSSProperties = {
    fontFamily: element.fontFamily || undefined,
    fontWeight: element.fontWeight || undefined,
    color: element.textColor || undefined
  };
  const titleStyle: React.CSSProperties = {
    ...textStyle,
    fontSize: `${titleSizePx}px`
  };
  const metaStyle: React.CSSProperties = {
    ...textStyle,
    fontSize: `${Math.max(10, Math.round(titleSizePx * 0.7))}px`
  };

  if (loading && items.length === 0) {
    return (
      <div className={styles.container} style={textStyle}>
        <span className={styles.source} style={metaStyle}>
          БЕЛТА
        </span>
        <span className={styles.loading} style={titleStyle}>
          Загрузка новостей...
        </span>
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <div className={styles.container} style={textStyle}>
        <span className={styles.source} style={metaStyle}>
          БЕЛТА
        </span>
        <span className={styles.error} style={titleStyle}>
          {error}
        </span>
      </div>
    );
  }

  if (!current) {
    return null;
  }

  return (
    <div className={styles.container} style={textStyle}>
      <div className={styles.header}>
        <span className={styles.source} style={metaStyle}>
          БЕЛТА
        </span>
        {items.length > 1 && (
          <span className={styles.counter} style={metaStyle}>
            {currentIndex + 1}/{items.length}
          </span>
        )}
      </div>
      <div className={`${styles.newsBody} ${fadeIn ? styles.fadeIn : styles.fadeOut}`}>
        {current.category && (
          <span className={styles.category} style={metaStyle}>
            {current.category}
          </span>
        )}
        <div className={styles.titleViewport} ref={viewportRef}>
          <div className={styles.title} ref={contentRef} style={titleStyle}>
            {current.title}
          </div>
        </div>
      </div>
    </div>
  );
};
